import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSessionTimeoutSeconds, getSessionExtensionTimeSeconds } from '../config/sessionConfig';
import { useAppState } from '../state/AppState';

interface ReservationTimerProps {
  onExpire?: () => void;
  style?: React.CSSProperties;
}

const pad = (n: number) => n.toString().padStart(2, '0');

/**
 * ReservationTimer - A countdown timer component for appointment reservations.
 *
 * Displays a countdown timer showing the remaining time for a reservation.
 * The timer automatically updates every second and can trigger a callback when
 * the time expires. Useful for appointment booking flows where users have
 * a limited time to complete their reservation.
 *
 * @param onExpire - Optional callback function called when the timer expires
 * @param style - Optional CSS styles to apply to the timer
 *
 * @example
 * ```tsx
 * <ReservationTimer
 *   onExpire={() => handleReservationExpired()}
 * />
 * ```
 */
const ReservationTimer: React.FC<ReservationTimerProps> = ({ onExpire, style }) => {
  const { state } = useAppState();
  const duration = getSessionTimeoutSeconds();
  const extensionTime = getSessionExtensionTimeSeconds(); // Configurable extension time
  const [remaining, setRemaining] = useState(duration);
  const navigate = useNavigate();
  
  const startTime = state.reservationStartTime || Date.now();

  useEffect(() => {
    const update = () => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000);
      const actualRemaining = Math.max(duration - elapsed, 0);
      
      // Show extension time less than actual time until we hit extension time real time
      if (actualRemaining > extensionTime) {
        // Show extension time less (from 15:00 to 0:00)
        setRemaining(Math.max(actualRemaining - extensionTime, 0));
      } else {
        // Show actual remaining time (from 5:00 to 0:00)
        setRemaining(actualRemaining);
      }
      
      if (actualRemaining === 0 && onExpire) onExpire();
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [startTime, onExpire, duration, extensionTime]);

  useEffect(() => {
    if (remaining === 0 && !onExpire) {
      navigate('/x');
    }
  }, [remaining, onExpire, navigate]);

  const min = Math.floor(remaining / 60);
  const sec = remaining % 60;

  return (
            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: '#185F93', fontSize: '1.1em', ...style }}>
      {pad(min)}:{pad(sec)}
    </span>
  );
};

export default ReservationTimer; 